import SectionHeading from "@/components/base/SectionHeading";

export interface AnatomyPart {
  icon: string;
  title: string;
  text: string;
}

interface CareAnatomyProps {
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  overlayTitle: string;
  overlayText: string;
  parts: AnatomyPart[];
  tone?: "light" | "tinted";
}

/**
 * Educational anatomy section — a large anatomical illustration beside a
 * labelled breakdown, written in simple patient-friendly language.
 */
export default function CareAnatomy({
  eyebrow,
  title,
  description,
  image,
  imageAlt,
  overlayTitle,
  overlayText,
  parts,
  tone = "light",
}: CareAnatomyProps) {
  const background = tone === "tinted" ? "bg-background-100" : "bg-background-50";

  return (
    <section className={`${background} py-16 md:py-24`}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="reveal relative lg:col-span-5">
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="h-[420px] w-full md:h-[560px]">
                <img
                  src={image}
                  alt={imageAlt}
                  title={imageAlt}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="absolute -bottom-5 left-4 right-4 flex items-start gap-3 rounded-2xl border border-background-200 bg-background-50/95 p-4 shadow-[0_20px_45px_-30px_rgba(15,40,80,0.5)] backdrop-blur md:left-6 md:right-auto md:w-[19rem]">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-body-scan-line text-xl" aria-hidden="true"></i>
              </span>
              <span>
                <span className="block font-heading text-sm font-semibold text-foreground-950">{overlayTitle}</span>
                <span className="mt-0.5 block font-body text-xs leading-relaxed text-foreground-600">
                  {overlayText}
                </span>
              </span>
            </div>
          </div>

          <div className="reveal lg:col-span-7">
            <SectionHeading eyebrow={eyebrow} title={title} description={description} align="left" />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {parts.map((part) => (
                <div key={part.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${part.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{part.title}</h3>
                  <p className="mt-1.5 font-body text-sm leading-relaxed text-foreground-600">{part.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}